import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import { z } from 'zod'

import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
} from '~/models/numberInputConstraint'

/** 値域と刻み (`numberInputConstraint.ts` の JEPX 定数の共通形) */
type JepxRange = {
  readonly min: number
  readonly max: number
  readonly step: number
}

/**
 * kind に対応する JEPX 値域 (価格 = MIN / MAX、 数量 = QTY / QTY_TOTAL)。
 *
 * QTY (上限数量(1札)) と QTY_TOTAL (上限数量(コマ計)) は別統制だが、 値域
 * (0.1〜9999.9 MW) は制度上限として同一 (docs/specs/bidding/bid-limits.md
 * §1-2「数量範囲はスポットと同じ」)。
 *
 * 値の SoT は app 共通の `~/models/numberInputConstraint` (スポット・時間前の
 * 入札入力欄と同じ制度上限。 backend `BidLimitValue` とも一致) で、 ここでは
 * 再定義せず kind への写像だけを持つ。
 *
 * QTY_TOTAL (上限数量(コマ計)) は QTY (上限数量(1札)) と同じ数量軸 (backend
 * `valueobject.BidKind.IsPrice()` が false を返す軸、bid-limits.md §1-2
 * 「数量範囲は 0.1〜9999.9 MW」) なので価格側に落としてはならない。価格側に
 * 落ちると、コマ計セルが画面に列を持たないまま (#8097 で追加予定) 他のセル
 * の保存に相乗りしてクランプされ、設定済みの上限が黙って書き換わる。
 */
export const bidLimitRangeOf = (kind: BidKind): JepxRange =>
  kind === BidKind.QTY || kind === BidKind.QTY_TOTAL
    ? JEPX_VOLUME_CONSTRAINT
    : JEPX_PRICE_CONSTRAINT

/**
 * 刻みから丸める小数桁を得る (0.01 → 2 / 0.1 → 1)。
 * `numberInputConstraint.ts` に同型の private helper があるが、 同ファイルは
 * 別レーンとの競合を避けて触らないため export を増やさずここに置く。
 */
const decimalsOfStep = (step: number): number => {
  const s = String(step)
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

/**
 * セル値を範囲内へクランプし、 刻みの桁数へ丸める
 * (`docs/specs/bidding/bid-limits.md` AC-BDL-007)。
 *
 * design `admin/bid-limits.html` の `_clampLimitValue` 準拠。 中間値 (12.25 等) は
 * 大きい側へ丸める。 mock-app `BidSettings.razor.cs` `MarkLimitEdit`
 * (code-behind 由来・要検証層) の素の `Math.Round(decimal, n)` は .NET 既定の
 * 銀行家丸めで 12.2 になるが、 mock-app が中間値の向きを**意図して**決めている
 * 箇所は `MidpointRounding.AwayFromZero` を明示しており
 * (`Services/CustomerBillingCalculator.cs`、 `Imputation/ImputationEngine.cs`、
 * react-app `models/customer/ratePlan.ts` ほか計 14 箇所)、 素の既定は選択の
 * 結果ではない。 クランプ後の値は必ず正 (>= 0.01 / >= 0.1) なので
 * AwayFromZero と half-up は一致し、 mock-app の明示規約・design・app 共通の
 * `violatesJepxStep` のいずれとも同じ結果になる。
 */
export const clampBidLimitCellValue = (
  value: number,
  kind: BidKind,
): number => {
  const range = bidLimitRangeOf(kind)
  const clamped = Math.min(range.max, Math.max(range.min, value))
  // `toFixed` は binary float の丸め誤差で 5.55 → 5.5 になる (5.55 * 10 = 55.4999…)。
  // design と同じく `toPrecision` で有効桁を整えてから丸める。
  const scale = 10 ** decimalsOfStep(range.step)
  return Math.round(parseFloat((clamped * scale).toPrecision(12))) / scale
}

/** タグ名: 1〜20 文字 */
export const tagNameSchema = z
  .string()
  .min(1, '1文字以上入力してください')
  .max(20, '20文字以内で入力してください')

/** matrix セル入力 (数値テキスト → number変換 or undefined = 未設定明示) */
export const cellInputSchema = z
  .string()
  .optional()
  .transform((v) => (v === '' || v === undefined ? undefined : parseFloat(v)))
  .pipe(z.number().optional())

/** BidTag 作成 form schema */
export const createBidTagSchema = z.object({
  name: tagNameSchema,
  variant: z.number().int().positive(),
  sortOrder: z.number().int().min(0),
})

export type CreateBidTagFormValues = z.infer<typeof createBidTagSchema>

/** BidTag 更新 form schema */
export const updateBidTagSchema = z.object({
  id: z.string().min(1),
  name: tagNameSchema,
  variant: z.number().int().positive(),
  sortOrder: z.number().int().min(0),
})

export type UpdateBidTagFormValues = z.infer<typeof updateBidTagSchema>
